import React, { useRef, useState, useEffect, useMemo } from "react";
import { Annotation, Point } from "../types/annotation.types";
import allImgPaths from "assets/images";

interface Props {
  imageSrc: string;
  // annotations: Annotation[];
  annotations: any[];
  onAddPoint: (point: Point) => void;
  tempPoints: { start?: Point; end?: Point };
  imageRef: any;
  canvasRef: any;
  imageScale?: number;
  fontInfo?: any;
  onUpdateAnnotation?: (index: number, ann: any, commit?: boolean) => void;
  color?: string;
}

const AnnotationCanvas2: React.FC<Props> = ({
  imageSrc,
  annotations,
  onAddPoint,
  tempPoints,
  imageRef,
  canvasRef,
  imageScale = 1, // Default scale is 1 (100%)
  fontInfo,
  onUpdateAnnotation,
  color = "red"
}) => {
  const [imgDims, setImgDims] = useState({ width: 0, height: 0 });

  const [fontSize, setFontSize] = useState<any>(fontInfo?.size || 24)
  const [fontName, setFontName] = useState<any>(fontInfo?.name?.replace(/\.ttf$/, "") || 'Arial')

  // Per-annotation text bounding boxes (canvas px), recorded on each draw for drag hit-testing.
  const textRectsRef = useRef<({ x: number; y: number; w: number; h: number } | null)[]>([]);
  const suppressClickRef = useRef(false);

  console.log({ annotations })

  const handleClick = (e: any) => {
    if (suppressClickRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Normalize coordinates to 0-1000 scale (like in ImageVisualizer)
    const xNorm = Math.round((x / rect.width) * 1000);
    const yNorm = Math.round((y / rect.height) * 1000);

    onAddPoint({ x: xNorm, y: yNorm });
  };

  const toCanvasPoint = (clientX: number, clientY: number) => {
    const rect = imageRef.current.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * imgDims.width,
      y: ((clientY - rect.top) / rect.height) * imgDims.height,
    };
  };

  const hitTextIndex = (p: Point) => {
    const rects = textRectsRef.current;
    for (let i = rects.length - 1; i >= 0; i--) {
      const r = rects[i];
      if (r && p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h)
        return i;
    }
    return -1;
  };

  // Arrow-tail attach point: on the border of the RENDERED text block (canvas
  // px), aimed at the tip, pushed out by a small gap so text never touches the
  // line. The stored text box is one line tall while rendering wraps to many —
  // anchoring to the stored box would cross the lower lines.
  const rectEdgePoint = (
    r: { x: number; y: number; w: number; h: number },
    tip: { x: number; y: number },
    gap: number = 8
  ) => {
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    const dx = tip.x - cx;
    const dy = tip.y - cy;
    const len = Math.hypot(dx, dy);
    if (!len) return { x: cx, y: cy };
    const t = Math.min(
      dx ? r.w / 2 / Math.abs(dx) : Infinity,
      dy ? r.h / 2 / Math.abs(dy) : Infinity,
      1
    );
    return { x: cx + dx * t + (dx / len) * gap, y: cy + dy * t + (dy / len) * gap };
  };

  const movedAnnotation = (
    orig: any,
    dxCanvas: number,
    dyCanvas: number,
    rect0: { x: number; y: number; w: number; h: number } | null
  ) => {
    const xScale = imgDims.width / 1000;
    const yScale = imgDims.height / 1000;
    if (orig.arrow_start_coordinate && orig.arrow_end_coordinate) {
      if (!orig.text_start_coordinate || !orig.text_end_coordinate) return orig;
      // isManual coords are 0-1000 normalized; Python coords are raw canvas px
      const dx = orig.isManual ? dxCanvas / xScale : dxCanvas;
      const dy = orig.isManual ? dyCanvas / yScale : dyCanvas;
      const ts = [orig.text_start_coordinate[0] + dx, orig.text_start_coordinate[1] + dy];
      const te = [orig.text_end_coordinate[0] + dx, orig.text_end_coordinate[1] + dy];
      let arrowStart = orig.arrow_start_coordinate;
      if (rect0) {
        const tipC = {
          x: orig.isManual ? orig.arrow_end_coordinate[0] * xScale : orig.arrow_end_coordinate[0],
          y: orig.isManual ? orig.arrow_end_coordinate[1] * yScale : orig.arrow_end_coordinate[1],
        };
        const movedRect = { x: rect0.x + dxCanvas, y: rect0.y + dyCanvas, w: rect0.w, h: rect0.h };
        const tailC = rectEdgePoint(movedRect, tipC);
        arrowStart = [
          orig.isManual ? tailC.x / xScale : tailC.x,
          orig.isManual ? tailC.y / yScale : tailC.y,
        ];
      }
      return {
        ...orig,
        text_start_coordinate: ts,
        text_end_coordinate: te,
        arrow_start_coordinate: arrowStart,
      };
    }
    // Direct annotation: text hangs off `end`, the head stays at `start`
    return {
      ...orig,
      end: { x: orig.end.x + dxCanvas / xScale, y: orig.end.y + dyCanvas / yScale },
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!onUpdateAnnotation || !imageRef.current || !imgDims.width) return;
    const p = toCanvasPoint(e.clientX, e.clientY);
    const index = hitTextIndex(p);
    if (index === -1) return;
    e.preventDefault();
    suppressClickRef.current = true;
    const orig = annotations[index];
    // Rendered text rect at drag start — its size is constant during the drag
    const rect0 = textRectsRef.current[index]
      ? { ...textRectsRef.current[index]! }
      : null;
    let latest = orig;
    const onMove = (ev: MouseEvent) => {
      const q = toCanvasPoint(ev.clientX, ev.clientY);
      latest = movedAnnotation(orig, q.x - p.x, q.y - p.y, rect0);
      onUpdateAnnotation(index, latest);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      onUpdateAnnotation(index, latest, true);
      setTimeout(() => (suppressClickRef.current = false), 0);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!onUpdateAnnotation || !imageRef.current || !imgDims.width) return;
    const over = hitTextIndex(toCanvasPoint(e.clientX, e.clientY)) !== -1;
    (e.currentTarget as HTMLElement).style.cursor = over ? "move" : "";
  };

  // Calculate dynamic font size based on image dimensions and scale factor
  const calculateDynamicFontSize = useMemo(() => {
    if (!imageRef.current || !imgDims.width || !imgDims.height) {
      return 22;
    }

    const avgDimension = (imgDims.width + imgDims.height) / 2;
    const baseFontSize = Math.min(
      32,
      Math.max(10, Math.round(avgDimension * 0.03))
    );

    let finalFontSize;
    if (imageScale <= 1.0) {
      finalFontSize = Math.max(10, Math.round(baseFontSize * imageScale * 1.0));
    } else {
      finalFontSize = Math.max(
        10,
        Math.round(baseFontSize / (imageScale * 1.0))
      );
    }

    return finalFontSize;
  }, [imgDims.width, imgDims.height, imageScale]);

  // Calculate dynamic arrow properties based on font size and image scale
  const calculateArrowProperties = useMemo(() => {
    const baseLineWidth = 2;
    const baseHeadLength = 10;

    let lineWidth, headLength;

    if (imageScale <= 1.0) {
      lineWidth = Math.max(1, Math.round(baseLineWidth * imageScale));
      headLength = Math.max(5, Math.round(baseHeadLength * imageScale));
    } else {
      lineWidth = Math.max(1, Math.round(baseLineWidth / imageScale));
      headLength = Math.max(5, Math.round(baseHeadLength / imageScale));
    }

    return { lineWidth, headLength };
  }, [imageScale]);

  const drawArrow = (
    ctx: CanvasRenderingContext2D,
    start: Point, // arrow start
    end: Point,   // arrow end (tip)
    text: string,
    textStart?: Point,
    textEnd?: Point,
    index?: number,
    fontSize: number = 16,
    lineHeight: number = 20
  ) => {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 2;
    ctx.font = `${fontSize}px ${fontName}`;
    ctx.textBaseline = "top";

    // 1. Draw arrow line
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    // 2. Draw arrowhead
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const headLength = 10;
    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
      end.x - headLength * Math.cos(angle - Math.PI / 6),
      end.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      end.x - headLength * Math.cos(angle + Math.PI / 6),
      end.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.lineTo(end.x, end.y);
    ctx.fill();

    // 3. Use text box position and width
    if (!textStart || !textEnd) return; // skip if no text box

    const padding = 2;
    const x = textStart.x + padding;
    const y = textStart.y + padding;
    const maxWidth = Math.abs(textEnd.x - textStart.x) - 2 * padding;

    // 4. Wrap and draw text
    const words = text.split(" ");
    let line = "";
    const lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + " ";
      if (ctx.measureText(testLine).width > maxWidth && i > 0) {
        lines.push(line.trim());
        line = words[i] + " ";
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());

    // Record the RENDERED block (stored box is one line tall; text wraps) for
    // drag hit-testing and arrow-tail anchoring.
    if (index !== undefined) {
      textRectsRef.current[index] = {
        x: Math.min(textStart.x, textEnd.x) - 4,
        y: Math.min(textStart.y, textEnd.y) - 4,
        w: Math.abs(textEnd.x - textStart.x) + 8,
        h: lines.length * lineHeight + 2 * padding + 8,
      };
    }

    // Draw each line
    lines.forEach((l, idx) => {
      ctx.fillText(l, x, y + idx * lineHeight);
    });
  };

  const draw = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, imgDims.width, imgDims.height);
    ctx.lineWidth = 2;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    // Draw the image first
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, imgDims.width, imgDims.height);
    }

    const xScale = imgDims.width / 1000;
    const yScale = imgDims.height / 1000;

    textRectsRef.current = [];

    annotations.forEach((ann, i) => {
      if (ann.arrow_start_coordinate && ann.arrow_end_coordinate) {
        const start = {
          x: ann?.isManual ? (ann.arrow_start_coordinate[0] * xScale) : ann.arrow_start_coordinate[0],
          y: ann?.isManual ? (ann.arrow_start_coordinate[1] * yScale) : ann.arrow_start_coordinate[1],
        };
        const end = {
          x: ann?.isManual ? (ann.arrow_end_coordinate[0] * xScale) : ann.arrow_end_coordinate[0],
          y: ann?.isManual ? (ann.arrow_end_coordinate[1] * yScale) : ann.arrow_end_coordinate[1],
        };
        const textStart = ann.text_start_coordinate
          ? {
            x: ann?.isManual ? (ann.text_start_coordinate[0] * xScale) : ann.text_start_coordinate[0],
            y: ann?.isManual ? (ann.text_start_coordinate[1] * yScale) : ann.text_start_coordinate[1],
          }
          : undefined;

        const textEnd = ann.text_end_coordinate
          ? {
            x: ann?.isManual ? (ann.text_end_coordinate[0] * xScale) : ann.text_end_coordinate[0],
            y: ann?.isManual ? (ann.text_end_coordinate[1] * yScale) : ann.text_end_coordinate[1],
          }
          : undefined;

        drawArrow(ctx, start, end, ann.label, textStart, textEnd, i);
      } else if (ann.start && ann.end) {
        const scaledStart = { x: ann.start.x * xScale, y: ann.start.y * yScale };
        const scaledEnd = { x: ann.end.x * xScale, y: ann.end.y * yScale };
        drawArrowOriginal(ctx, scaledStart, scaledEnd, ann.text, i);
      }
    });

    console.log({ tempPoints })

    // Draw temporary points/arrow
    if (tempPoints.start && !tempPoints.end) {
      // Convert normalized coordinates to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;
      const scaledX = tempPoints.start.x * xScale;
      const scaledY = tempPoints.start.y * yScale;

      ctx.beginPath();
      ctx.arc(scaledX, scaledY, 5, 0, 2 * Math.PI);
      ctx.fill();
    } else if (tempPoints.start && tempPoints.end) {
      // Convert normalized coordinates to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;

      const end = {
        x: tempPoints.start.x * xScale,
        y: tempPoints.start.y * yScale,
      };
      const start = {
        x: tempPoints.end.x * xScale,
        y: tempPoints.end.y * yScale,
      };
      drawArrow(ctx, start, end, "");
    }
  };

  const drawArrowOriginal = (
    ctx: CanvasRenderingContext2D,
    start: Point,
    end: Point,
    text: string,
    index?: number
  ) => {
    const { headLength, lineWidth } = calculateArrowProperties;

    const dx = start.x - end.x;
    const dy = start.y - end.y;
    const angle = Math.atan2(dy, dx);

    ctx.beginPath();
    ctx.lineWidth = lineWidth;
    const extension = 50;
    const extendedEndX = end.x - extension * Math.cos(angle);
    const extendedEndY = end.y - extension * Math.sin(angle);

    // ctx.moveTo(end.x, end.y);
    ctx.moveTo(extendedEndX, extendedEndY);
    ctx.lineTo(start.x, start.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(
      start.x - headLength * Math.cos(angle - Math.PI / 6),
      start.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      start.x - headLength * Math.cos(angle + Math.PI / 6),
      start.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.lineTo(start.x, start.y);
    ctx.fill();

    if (!text) return;

    ctx.font = `${calculateDynamicFontSize}px Arial`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = color;

    const arrowLength = Math.sqrt(
      Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
    );
    const textOffset = Math.max(80, arrowLength * 0.5);
    const reverseAngle = Math.atan2(end.y - start.y, end.x - start.x);
    let textX = end.x + textOffset * Math.cos(reverseAngle);
    let textY = end.y + textOffset * Math.sin(reverseAngle);

    const maxWidth = 250;
    const lineHeight = 24;
    const padding = 10;

    const words = text.split(" ");
    let line = "";
    const lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + " ";
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && i > 0) {
        lines.push(line);
        line = words[i] + " ";
      } else {
        line = testLine;
      }
    }
    lines.push(line);

    const textWidth = lines.reduce((max, l) => {
      return Math.max(max, ctx.measureText(l).width);
    }, 0);
    const textHeight = lines.length * lineHeight;

    // 🛑 Clamp textX and textY to ensure the box stays within canvas bounds
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;

    if (textX + textWidth + padding > canvasWidth) {
      textX = canvasWidth - textWidth - padding;
    }
    if (textY + textHeight + padding > canvasHeight) {
      textY = canvasHeight - textHeight - padding;
    }

    if (textX < padding) textX = padding;
    if (textY < padding) textY = padding;

    if (index !== undefined) {
      textRectsRef.current[index] = {
        x: textX - 4,
        y: textY - 4,
        w: textWidth + 8,
        h: textHeight + 8,
      };
    }

    lines.forEach((line, idx) => {
      ctx.fillText(line, textX, textY + idx * lineHeight);
    });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && imgDims.width > 0) {
      const ctx = canvas.getContext("2d");
      if (ctx) draw(ctx);
    }
  }, [annotations, tempPoints, imgDims, imageRef, imageScale, color]);

  return (
    <div className="relative w-full max-w-full mx-auto min-h-[55rem]">
      <img
        ref={imageRef}
        src={imageSrc}
        crossOrigin="anonymous"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        draggable={false}
        onLoad={(e) => {
          const img = e.currentTarget;
          setImgDims({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }}
        style={{
          display: "block",
          maxWidth: "100%",
          transform: `scale(${imageScale})`,
          transformOrigin: "top left",
        }}
        alt="Annotatable"
      />
      <canvas
        ref={canvasRef}
        width={imgDims.width}
        height={imgDims.height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          transform: `scale(${imageScale})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
};

export default AnnotationCanvas2;
